import { wrap } from 'comlink';
import type { Remote } from 'comlink';
import * as _ from 'lodash-es';
import Block from 'quill/blots/block';
import Quill, { Delta } from 'quill/core';
import type { EmitterSource } from 'quill/core';
import { useEffect, useState } from 'react';
import getSampleWords from './getSampleWords';
import type spellCorrectorWorker from '../../workers/spellCorrector/spellCorrector';
import { useQuill } from '../QuillContext';
import type { SpellCorrectionResult } from './correctPrecedingWord';
import correctPrecedingWord from './correctPrecedingWord';
import createWorker from './createWorker';
import * as styles from './useSpellCorrector.module.scss';
import useAppSelector from '../../app/redux/useAppSelector';
import {
  ACTION_AI_SPELLCHECK_AUTO,
  CATEGORY_PRODUCT,
} from '../../constants/analytics';
import useAllUsers from '../../user/useAllUsers';
import { track } from '../../utils/analytics';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import { ENDING_PUNCTUATIONS } from '../constants';
import CodeEmbed from '../formats/CodeEmbed';
import { findBlotByNode, findQuillByNode } from '../utils/findByNode';

const TRIGGER_CHARACTERS = new Set([' ', ...ENDING_PUNCTUATIONS]);

const replaceWord = (
  quill: Quill,
  index: number,
  word: SpellCorrectionResult,
) => {
  const start = index - word.original.length;
  if (
    !quill
      .getText(start, word.original.length + 1)
      .match(new RegExp(`^${_.escapeRegExp(word.original)}($|\\W)`))
  ) {
    // Browser's built-in spell checker may have corrected the word.
    return false;
  }
  quill.history.cutoff();
  quill.updateContents(
    new Delta()
      .retain(start)
      .delete(word.original.length)
      .insert(`${word.corrected}`, word.formats),
    Quill.sources.USER,
  );
  quill.history.cutoff();

  quill.setSelection(
    index + 1 - (word.original.length - word.corrected.length),
    Quill.sources.SILENT,
  );

  const bounds = quill.getBounds(
    index - word.original.length,
    word.corrected.length,
  );
  if (bounds) {
    const indicator = quill.root.ownerDocument.createElement('div');
    indicator.classList.add(styles.indicator);
    Object.assign(indicator.style, {
      left: `${bounds.left}px`,
      top: `${bounds.bottom}px`,
      width: `${bounds.width}px`,
    });
    quill.addContainer(indicator);
    setTimeout(() => {
      indicator.remove();
    }, 2000);
  }

  return true;
};

const getTargetQuill = (event: KeyboardEvent) => {
  const blot =
    event.target instanceof Node ? findBlotByNode(event.target, true) : null;
  return blot?.scroll.domNode.parentNode
    ? findQuillByNode(blot.scroll.domNode.parentNode)
    : null;
};

const useLazyLoadWorker = (shouldEnable: boolean) => {
  type State =
    | {
        isEnglishContent: true;
        worker: Remote<typeof spellCorrectorWorker>;
        destroyWorker: () => void;
      }
    | { isEnglishContent: false }
    | null;

  const { quill } = useQuill();
  const [state, setState] = useState<State>(null);

  useEffect(() => {
    if (!shouldEnable || !quill || state !== null) {
      return undefined;
    }

    const listener = (
      delta: Delta,
      _contents: Delta,
      source: EmitterSource,
    ) => {
      const isContentInserted =
        source === Quill.sources.USER &&
        delta.ops.some((op) => typeof op.insert === 'string');
      if (!isContentInserted) return;

      const sampleWords = getSampleWords(quill);
      if (!sampleWords) return;

      quill.off(Quill.events.TEXT_CHANGE, listener);

      const [rawWorker, destroyWorker] = createWorker(
        window.__slab.WORKERS?.spellCorrector ?? '',
      );
      const worker = wrap<Remote<typeof spellCorrectorWorker>>(rawWorker);
      worker.getValidWordCount(sampleWords).then((count) => {
        const isEnglishContent = count > sampleWords.length / 2;
        if (isEnglishContent) {
          setState({ isEnglishContent: true, worker, destroyWorker });
        } else {
          setState({ isEnglishContent: false });
        }
      });
    };

    quill.on(Quill.events.TEXT_CHANGE, listener);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, listener);
    };
  }, [shouldEnable, quill, state]);

  useEffect(
    () => () => {
      if (state?.isEnglishContent) {
        state.destroyWorker();
      }
    },
    [state],
  );
  return state?.isEnglishContent ? state.worker : null;
};

const useSpellCorrector = (postId: string) => {
  const { quill, readOnly } = useQuill();
  const isSettingEnabled = useAppSelector(
    (state) => state.session.user.settings?.aiAutofix ?? false,
  );
  const isMobile = useIsSize(Sizes.SM);

  const shouldEnable = isSettingEnabled && !readOnly && !isMobile;
  const worker = useLazyLoadWorker(shouldEnable);

  const allUsers = useAllUsers();
  const organizationName = useAppSelector(
    (state) => state.session.organization.name,
  );

  useEffect(() => {
    worker?.insertNames([
      ...allUsers.map((user) => user.name),
      organizationName,
    ]);
  }, [allUsers, organizationName, worker]);

  useEffect(() => {
    if (!shouldEnable || !quill || !worker) {
      return undefined;
    }

    const getContext = (
      event: KeyboardEvent,
    ): [Quill, number] | [null, null] => {
      const targetQuill = getTargetQuill(event);
      if (!targetQuill) return [null, null];
      if (
        targetQuill === quill ||
        !(quill.scroll.find(targetQuill.container, true) instanceof CodeEmbed)
      ) {
        const range = targetQuill?.getSelection();
        if (range?.length === 0) {
          return [targetQuill, range.index];
        }
      }
      return [null, null];
    };

    let lastOriginalWord: string | null = null;
    const correctAndReplacePrecedingWord = async (
      targetQuill: Quill,
      index: number,
    ) => {
      const word = await correctPrecedingWord(
        targetQuill,
        index,
        worker.lookupWord,
      );
      if (!word) return;

      if (word.original === lastOriginalWord) {
        lastOriginalWord = null;
      } else {
        lastOriginalWord = word.original;

        const isReplaced = replaceWord(targetQuill, index, word);
        if (isReplaced) {
          track(ACTION_AI_SPELLCHECK_AUTO, {
            category: CATEGORY_PRODUCT,
            label: postId,
            original: word.original,
            corrected: word.corrected,
          });
        }
      }
    };

    const handler = (event: KeyboardEvent) => {
      if (event.isComposing) return;

      if (event.key === 'Enter') {
        const [targetQuill, index] = getContext(event);
        if (targetQuill) {
          const prevIndex = index - 1;
          const [previousLine, previousLineOffset] =
            targetQuill.getLine(prevIndex);
          if (
            previousLine instanceof Block &&
            previousLineOffset === previousLine.length() - 1
          ) {
            correctAndReplacePrecedingWord(targetQuill, prevIndex);
          }
        }
      } else if (!event.defaultPrevented && TRIGGER_CHARACTERS.has(event.key)) {
        const [targetQuill, index] = getContext(event);
        if (targetQuill) {
          correctAndReplacePrecedingWord(targetQuill, index);
        }
      }
    };

    quill.root.addEventListener('keydown', handler);
    return () => {
      quill.root.removeEventListener('keydown', handler);
    };
  }, [shouldEnable, quill, worker, postId]);
};

export default useSpellCorrector;
